
const SolarPlans = () => {
  const plans = [
    {
      id: 1,
      capacity: "2 kW",
      title: "Starter Solar System",
      totalCost: "₹1,40,000",
      subsidy: "₹60,000",
      finalCost: "₹80,000",
      specialBenefit: "Additional ₹50,000 subsidy for BPL card holders",
      idealFor: "Small homes and low-consumption households",
      features: [
        "Reduces electricity bill significantly",
        "Backup power during outages",
        "Easy installation and maintenance",
        "25-year panel warranty"
      ],
      popular: false,
      gradient: "from-blue-500 to-blue-600",
      icon: "ri-home-4-line"
    },
    {
      id: 2,
      capacity: "3 kW",
      title: "Family Solar System",
      totalCost: "₹1,80,000",
      subsidy: "₹78,000",
      finalCost: "₹1,02,000",
      idealFor: "Medium-size homes or small businesses",
      features: [
        "Handles heavy appliance loads",
        "Uninterrupted power supply",
        "Advanced grid-tie technology",
        "25-year panel warranty"
      ],
      popular: true,
      gradient: "from-emerald-500 to-emerald-600",
      icon: "ri-home-smile-line"
    },
    {
      id: 3,
      capacity: "5 kW",
      title: "Premium Solar System",
      totalCost: "₹2,70,000",
      subsidy: "₹78,000",
      finalCost: "₹1,92,000",
      idealFor: "Large homes and commercial setups",
      features: [
        "Handles heavy appliance loads",
        "Uninterrupted power supply",
        "Advanced grid-tie technology",
        "25-year panel warranty"
      ],
      popular: false,
      gradient: "from-amber-500 to-amber-600",
      icon: "ri-building-line"
    }
  ];

  return (
    <section id="plans" className="py-20 bg-gradient-to-br from-gray-50 to-white">
      <div className="container mx-auto px-6">
        <div className="text-center mb-16">
          <div className="inline-flex items-center bg-emerald-100 text-emerald-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
            <i className="ri-government-line mr-2"></i>
            PM Surya Ghar Yojana 2025 Plans
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Choose Your Perfect
            <span className="block text-transparent bg-gradient-to-r from-emerald-600 to-amber-500 bg-clip-text">
              Solar Solution
            </span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Government-backed solar plans designed for every home size and budget. 
            Get maximum subsidies and start saving from day one.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
          {plans.map((plan) => (
            <div
              key={plan.id}
              className={`relative bg-white rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 ${
                plan.popular ? 'ring-4 ring-emerald-500/20 scale-105' : ''
              }`}
            >
              {plan.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 text-white px-6 py-2 rounded-full text-sm font-bold shadow-lg">
                    Most Popular
                  </div>
                </div>
              )}

              <div className="p-8">
                {/* Header */}
                <div className="text-center mb-8">
                  <div className={`w-20 h-20 bg-gradient-to-r ${plan.gradient} rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg`}>
                    <i className={`${plan.icon} text-white text-3xl`}></i>
                  </div>
                  <div className="text-3xl font-bold text-gray-900 mb-2">{plan.capacity}</div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-4">{plan.title}</h3>
                  <div className="text-sm text-gray-600 bg-gray-50 rounded-lg p-3">
                    {plan.idealFor}
                  </div>
                </div>

                {/* Pricing */}
                <div className="mb-8 p-6 bg-gradient-to-r from-gray-50 to-gray-100 rounded-2xl">
                  <div className="flex justify-between items-center mb-3">
                    <span className="text-gray-600">Total Cost:</span>
                    <span className="text-lg font-semibold text-gray-900">{plan.totalCost}</span>
                  </div>
                  <div className="flex justify-between items-center mb-3">
                    <span className="text-emerald-600">Government Subsidy:</span>
                    <span className="text-lg font-bold text-emerald-600">-{plan.subsidy}</span>
                  </div>
                  <div className="border-t border-gray-200 pt-3">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-900 font-semibold">Your Cost:</span>
                      <span className={`text-2xl font-bold bg-gradient-to-r ${plan.gradient} bg-clip-text text-transparent`}>
                        {plan.finalCost}
                      </span>
                    </div>
                  </div>
                  {plan.specialBenefit && (
                    <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                      <div className="text-xs text-amber-700 font-medium">Special Benefit:</div>
                      <div className="text-sm text-amber-800">{plan.specialBenefit}</div>
                    </div>
                  )}
                </div>

                {/* Features */}
                <div className="mb-8">
                  <h4 className="font-semibold text-gray-900 mb-4">Key Features:</h4>
                  <ul className="space-y-3">
                    {plan.features.map((feature, index) => (
                      <li key={index} className="flex items-start">
                        <div className="w-5 h-5 bg-emerald-100 rounded-full flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                          <i className="ri-check-line text-emerald-600 text-sm"></i>
                        </div>
                        <span className="text-gray-700 text-sm leading-relaxed">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* CTA Button */}
                <div className="space-y-3">
                  <a
                    href="tel:9416407685"
                    className={`w-full bg-gradient-to-r ${plan.gradient} text-white py-4 px-6 rounded-xl font-semibold hover:shadow-lg transition-all duration-300 text-center block whitespace-nowrap cursor-pointer transform hover:scale-105`}
                  >
                    Get This Plan
                  </a>
                  <a
                    href="#contact"
                    className="w-full border-2 border-gray-200 text-gray-700 py-3 px-6 rounded-xl font-medium hover:border-emerald-500 hover:text-emerald-600 transition-all duration-300 text-center block whitespace-nowrap cursor-pointer"
                  >
                    Learn More
                  </a>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-16">
          <div className="bg-gradient-to-r from-emerald-600 to-amber-500 rounded-3xl p-8 text-white max-w-4xl mx-auto">
            <h3 className="text-2xl md:text-3xl font-bold mb-4">
              Need a Custom Solar Solution?
            </h3>
            <p className="text-emerald-100 mb-6 text-lg">
              Our experts will design the perfect system for your specific needs and budget
            </p>
            <a
              href="tel:7496038344"
              className="inline-flex items-center bg-white text-emerald-600 px-8 py-4 rounded-full font-semibold hover:bg-gray-50 transition-all duration-300 whitespace-nowrap cursor-pointer shadow-lg"
            >
              <i className="ri-phone-line mr-2"></i>
              Get Free Consultation
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SolarPlans;
